import { Box, Stack } from '@chakra-ui/react';
import { Divider, Grid, gridConfig } from '@time-line/components/common';
import { CountrySelector } from 'components/localisation/CountrySelector';
import { useGlobals } from 'hooks/useGlobals';
import { useIsSkincare } from 'hooks/useIsSkincare';
import { type IndexedPageFootnote } from 'hooks/usePageFootnotes';
import { env } from 'lib/constants/env';

import { FOOTER_CONTENT } from '../constants';
import { Disclaimers } from '../Disclaimers';
import { Hero } from '../Hero';
import { Meta } from '../Meta';
import { NewsletterSignup } from '../NewsletterSignup';
import { type DisclaimerContent } from '../useDisclaimers';
import { useFooterInputs } from '../useFooterInputs';

const { borderWidthClamp, gridBorderRadius } = gridConfig;

type CampaignFooterProps = {
  disclaimers?: DisclaimerContent | null;
  pageFootnotes?: IndexedPageFootnote[];
};

export const CampaignFooter = ({
  disclaimers: disclaimersProp,
  pageFootnotes: pageFootnotesProp,
}: CampaignFooterProps = {}) => {
  const { legal: supplementsLegal, skincareLegal } = FOOTER_CONTENT;
  const { data: globals } = useGlobals();
  const isSkincare = useIsSkincare();
  const { disclaimers, pageFootnotes } = useFooterInputs({
    disclaimers: disclaimersProp,
    pageFootnotes: pageFootnotesProp,
  });

  const footerHeading = globals?.siteSettings.footerHeading;
  const legal = isSkincare ? skincareLegal : supplementsLegal;

  return (
    <Box className="footer" as="footer" color="gray.600">
      <Grid
        pos="relative"
        pb="m"
        pt={{ base: 'm', lg: 'lg' }}
        withBorder
        bgColor="gray.100"
        borderRadius={gridBorderRadius}
        mb={borderWidthClamp}
      >
        <Hero heading={footerHeading} />

        <Grid.Inner mb="sm" gridRowGap="lg">
          <Stack
            justifyContent="space-between"
            gridColumn={{ base: 'span 12', lg: 'span 4' }}
            direction="column"
            gap="2xs"
          >
            <NewsletterSignup
              headingLabel="Sign up for our newsletter"
              hideHeadingOnDesktop={false}
            />
            {env.NEXT_PUBLIC_COUNTRY_SELECTOR_ENABLED && (
              <Box alignSelf="flex-start">
                <CountrySelector showFullName variant="overline" />
              </Box>
            )}
          </Stack>
          {(disclaimers || pageFootnotes.length > 0) && (
            <Box gridColumn={{ base: 'span 12', lg: '7 / -1' }}>
              <Disclaimers
                disclaimers={disclaimers}
                pageFootnotes={pageFootnotes}
              />
            </Box>
          )}
        </Grid.Inner>

        {!disclaimers && pageFootnotes.length === 0 && (
          <Grid.Inner mb="sm">
            <Box gridColumn="span 12">
              <Divider addOpacity />
            </Box>
          </Grid.Inner>
        )}

        <Meta legal={legal} />
      </Grid>
    </Box>
  );
};
